import { faSquare } from "@fortawesome/free-solid-svg-icons";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Box,
    CardContent,
    CardHeader,
    CardProps,
    Chip,
    IconButtonProps,
    Card as MuiCard,
    IconButton as MuiIconButton,
    styled,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import ElementDetails from "src/dialogs/ElementDetails";
import { Type_Element, Type_Element_Data } from "src/types/element";
import { getIconFromPicto } from "src/utils/category";

const IconButton = styled(MuiIconButton)<IconButtonProps>(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: "0.875rem",
    borderRadius: theme.spacing(1),
}));

const Icon = styled(FontAwesomeIcon)(({ theme }) => ({
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    fontSize: "1rem",
}));

const Card = styled(MuiCard)<CardProps>(({ theme }) => ({
    [theme.breakpoints.down("md")]: {
        width: "100vw",
    },
}));

const HoverCard = () => {
    const { t } = useTranslation();
    const {
        newElement: element,
        elements,
        categories,
    } = useContext(MapContext);

    const [showDetails, setShowDetails] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [clicked, setClicked] = useState(false);

    const parent = elements.find(
        (item: Type_Element_Data) => item.id === element?.parent_id,
    );

    const theme = useTheme();

    if (!element || !categories) return null;

    const elementCategory = (elem: Type_Element) =>
        elem
            ? categories.find((category) => category.id === elem.category_id)
            : null;

    const textColor = theme.palette.getContrastText(
        !element?.style
            ? (elementCategory(element)?.style.primary_color as string)
            : (element.style.primary_color as string),
    );

    const handleCloseDetails = () => setShowDetails(false);

    const isOpen = hovered || clicked;

    const { language } = useContext(LanguageContext);

    const categoryChip = (element: Type_Element) =>
        elementCategory(element) && (
            <Chip
                size="small"
                sx={{
                    padding: 1,
                    color: textColor,
                    borderColor: textColor,
                }}
                label={elementCategory(element)?.title[language]}
                variant="outlined"
                icon={
                    <FontAwesomeIcon
                        color={textColor}
                        icon={
                            elementCategory(element)?.picto
                                ? getIconFromPicto(
                                      elementCategory(element)?.picto || "",
                                  )
                                : faSquare
                        }
                    />
                }
            />
        );

    if (!element || !categories) return null;

    return (
        <>
            <Tooltip
                PopperProps={{
                    sx: {
                        "& .MuiTooltip-tooltip": {
                            backgroundColor: theme.palette.transparent.main,
                            maxWidth: "max-content",
                        },
                    },
                }}
                title={
                    <Card
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                    >
                        <CardHeader
                            style={{
                                backgroundColor: !element.style
                                    ? elementCategory(element)?.style
                                          .primary_color
                                    : element.style.primary_color,
                                textColor,
                                color: textColor,
                            }}
                            action={
                                <IconButton
                                    disableRipple
                                    onClick={() => {
                                        if (!showDetails) setShowDetails(true);
                                        setClicked(false);
                                        setHovered(false);
                                    }}
                                >
                                    <Icon
                                        color={textColor}
                                        icon={faArrowUpRightFromSquare}
                                    />
                                </IconButton>
                            }
                            title={
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-start"
                                    gap={0.5}
                                >
                                    {parent && (
                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            gap={1}
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    elementCategory(parent)
                                                        ?.picto
                                                        ? getIconFromPicto(
                                                              elementCategory(
                                                                  parent,
                                                              )?.picto || "",
                                                          )
                                                        : faSquare
                                                }
                                                fontSize="0.8rem"
                                            />
                                            <Typography fontSize="0.8rem">
                                                {parent.title}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Typography>
                                        <strong>{element.title}</strong>
                                    </Typography>

                                    {categoryChip(element)}
                                </Box>
                            }
                        />
                        <CardContent>
                            <Typography fontSize="small" color="text.secondary">
                                <Box component="span" mr={1}>
                                    {t("period")}:
                                </Box>
                                <strong>
                                    <Box
                                        component="span"
                                        mr={1}
                                        color={theme.palette.common.black}
                                    >
                                        {dayjs(element.start_date).format(
                                            "L LT",
                                        )}
                                    </Box>
                                </strong>
                                <Box component="span" mr={1}>
                                    {t("until")}
                                </Box>
                                <strong>
                                    <Box
                                        component="span"
                                        mr={1}
                                        color={theme.palette.common.black}
                                    >
                                        {dayjs(element.end_date).format("L LT")}
                                    </Box>
                                </strong>
                            </Typography>
                        </CardContent>
                    </Card>
                }
                open={isOpen}
            >
                <Box
                    onClick={() => setClicked(!clicked)}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    style={{ cursor: "pointer" }}
                >
                    <Typography color="primary">.</Typography>
                </Box>
            </Tooltip>

            {showDetails && <ElementDetails close={handleCloseDetails} />}
        </>
    );
};

export default HoverCard;
